import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  TextField,
  Tooltip,
  Popover,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { getExpertAnalytics, getExpertAnalyticsDownload, setDownloadAnalyticsByExpertStatus } from '../../redux/slices/analytics/experts';
import { STATUS } from '../../utils/status';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import SnackbarNotification from '../snackbar/snackbarNotification';
import { usePollingTaskStatus } from '../outputJson/usePollingStatus';
import TaskStatusModal from '../outputJson/taskStatusModal';
import { getOutputJsonTaskStatus, setTaskStatus } from '../../redux/slices/outputJson';
import { getReview } from '../../redux/slices/reviewerSlice';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { AnalyticsGranularity, getDateRangeForGranularity } from '../../utils/helper';
import Error from '../error/error';

const PAGE_SIZE = 10;

const toNumber = (value: any): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatScore = (value: any) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return parsed.toFixed(2);
  }

  return String(value);
};

const getExpertDisplayName = (expert: any) => expert?.name || expert?.pseudo_name || expert?.email || 'Unnamed Expert';
const getExpertGigId = (expert: any) => expert?.expert_gigsource_id || expert?.gig_id || expert?.gigsource_id || expert?.expert_id || 'N/A';
const getExpertNameValue = (expert: any) => String(expert?.name || expert?.pseudo_name || '').trim();
const getExpertEmailValue = (expert: any) => String(expert?.email || '').trim();
const getAcceptedTotal = (expert: any) =>
  toNumber(expert?.total_quality_accepted_as_is ?? expert?.accepted_as_is_count ?? 0) +
  toNumber(expert?.total_quality_accepted_with_edits ?? expert?.accepted_with_edits_count ?? 0);

const TaskByExperts = () => {
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStageId, setSelectedStageId] = useState<string>('');
  const dayDateRange = React.useMemo(() => getDateRangeForGranularity('day'), []);
  const [startDate, setStartDate] = useState(dayDateRange.startDate);
  const [endDate, setEndDate] = useState(dayDateRange.endDate);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [granularity, setGranularity] = useState<AnalyticsGranularity>('day');
  const [appliedStartDate, setAppliedStartDate] = useState(dayDateRange.startDate);
  const [appliedEndDate, setAppliedEndDate] = useState(dayDateRange.endDate);
  const [selectedExpertId, setSelectedExpertId] = useState<string | null>(null);
  const association_type = useSelector((state: RootState) => state.projects.association_type);
  const { downloadAnalyticsByExpertStatus, analyticsExpertDownload, getExpertAnalyticsDownloadMessage } = useSelector((state: RootState) => state.analyticsExpert);
  const expertAnalyticsStatus = useSelector((state: RootState) => state.analyticsExpert.getStatus);
  const analyticsExpertCount = useSelector((state: RootState) => state.analyticsExpert.analyticsExpert?.count || 0);
  // Expert analytics rows carry fields (stage_score, expert_id, aht, etc.) not modeled on the shared CreateAnalyticsPayload type.
  const expertResults: any[] = useSelector((state: RootState) => state.analyticsExpert.analyticsExpert?.results || []);
  const reviewStages = useSelector((state: RootState) => state.reviewStage.reviewer?.results || []);

  const stageOptions = React.useMemo(() => {
    return reviewStages.filter((stage: any) => String(stage?.stage_type || stage?.category || '').toUpperCase() === 'TASK');
  }, [reviewStages]);

  const filteredExperts = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    return expertResults.filter((expert: any) => {
      if (!term) return true;
      const name = getExpertDisplayName(expert).toLowerCase();
      const gigId = String(getExpertGigId(expert)).toLowerCase();
      const email = String(expert?.email || '').toLowerCase();
      return name.includes(term) || gigId.includes(term) || email.includes(term);
    });
  }, [expertResults, searchQuery]);

  const selectedExpert = filteredExperts.find((expert: any) => (expert?.expert_id || expert?.email) === selectedExpertId) || filteredExperts[0] || null;

  const stageScoreEntries = useMemo(() => {
    if (!selectedExpert?.stage_score || typeof selectedExpert.stage_score !== 'object') {
      return [] as Array<{ name: string; score: number }>; 
    }

    return Object.entries(selectedExpert.stage_score)
      .map(([name, score]) => ({ name, score: Number(score) || 0 }))
      .sort((a, b) => a.score - b.score);
  }, [selectedExpert]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  const handleMenuClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadForTask = () => {
    dispatch(getExpertAnalyticsDownload({
      project_id: id,
      type: 'TASK',
      chip: 'taskers',
      stage_id: selectedStageId || undefined,
      search: searchQuery || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
      granularity,
      start_date: appliedStartDate || undefined,
      end_date: appliedEndDate || undefined,
      association_type: association_type,
    }));
    handleMenuClose();
  };

  const handleDownloadForReview = () => {
    dispatch(getExpertAnalyticsDownload({
      project_id: id,
      type: 'REVIEW',
      chip: 'taskers',
      stage_id: selectedStageId || undefined,
      search: searchQuery || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
      granularity,
      start_date: appliedStartDate || undefined,
      end_date: appliedEndDate || undefined,
      association_type: association_type,
    }));
    handleMenuClose();
  };

  useEffect(() => {
    if (id) {
      dispatch(getReview({ project_id: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && appliedStartDate && appliedEndDate) {
      // Search is intentionally not sent to the backend here: the API only matches
      // name/email, which hides experts searched by Gig Source ID or expert ID.
      // The full expert list is fetched and filtered client-side (see filteredExperts) instead.
      dispatch(getExpertAnalytics({
        project_id: id,
        chip: 'taskers',
        stage_id: selectedStageId || undefined,
        granularity,
        start_date: appliedStartDate || undefined,
        end_date: appliedEndDate || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: currentPage,
        pageSize: PAGE_SIZE,
        tab: 'experts',
        association_type: association_type,
      }));
    }
  }, [dispatch, id, association_type, selectedStageId, sortBy, sortOrder, currentPage, granularity, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(1);
    }, 300);

    return () => {
      window.clearTimeout(debounceTimer);
    };
  }, [searchInput]);

  useEffect(() => {
    if (!selectedStageId) {
      return;
    }

    const hasSelectedStage = stageOptions.some((stage: any) => String(stage.id) === selectedStageId);
    if (!hasSelectedStage) {
      setSelectedStageId('');
    }
  }, [selectedStageId, stageOptions]);

  useEffect(() => {
    if (filteredExperts.length === 0) {
      setSelectedExpertId(null);
      return;
    }

    const current = filteredExperts.find((expert: any) => (expert?.expert_id || expert?.email) === selectedExpertId);
    if (!current && selectedExpertId !== null) {
      setSelectedExpertId(filteredExperts[0]?.expert_id || filteredExperts[0]?.email || null);
      return;
    }

    if (!selectedExpertId) {
      setSelectedExpertId(filteredExperts[0]?.expert_id || filteredExperts[0]?.email || null);
    }
  }, [filteredExperts, selectedExpertId]);

  const handleColumnSort = (sortField: string): void => {
    if (sortBy === sortField) {
      setSortOrder((previousOrder) => (previousOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(sortField);
      setSortOrder('asc');
    }

    setCurrentPage(1);
  };

  const handleApplyFilters = (): void => {
    setCurrentPage(1);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setFilterAnchorEl(null);
  };

  const handleClearFilters = (): void => {
    const dateRange = getDateRangeForGranularity(granularity);
    setSearchInput('');
    setSearchQuery('');
    setSelectedStageId('');
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
    setAppliedStartDate(dateRange.startDate);
    setAppliedEndDate(dateRange.endDate);
    setCurrentPage(1);
    setFilterAnchorEl(null);
  };

  const totalPages = Math.max(1, Math.ceil((analyticsExpertCount || 0) / PAGE_SIZE));
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const { getTaskStatusOutputJson, getTaskStatusOutputJsonMessage, taskStatusOutputJsonResponse } = useSelector((state: RootState) => state.outputJson);
  const [openTaskStatusModal, setOpenTaskStatusModal] = useState(false);

  React.useEffect(() => {
    const status = taskStatusOutputJsonResponse?.data?.status;
    if (status === 'PENDING' || status === 'SUCCESS' || status === 'FAILURE') {
      setOpenTaskStatusModal(true);
    }
  }, [taskStatusOutputJsonResponse]);

  const taskId =
    downloadAnalyticsByExpertStatus === STATUS.SUCESS &&
      analyticsExpertDownload &&
      typeof analyticsExpertDownload === 'object' &&
      'task_id' in analyticsExpertDownload
      ? (analyticsExpertDownload as { task_id: string }).task_id
      : undefined;

  usePollingTaskStatus({
    status: taskStatusOutputJsonResponse?.data?.status,
    taskId,
    onSuccess: () => {
      console.log('Polling finished successfully');
    },
    onFailure: () => {
      console.log('Polling failed');
    },
  });

  React.useEffect(() => {
    if (getTaskStatusOutputJson === STATUS.SUCESS && taskStatusOutputJsonResponse?.data?.url) {
      const url = taskStatusOutputJsonResponse.data.url;
      setSnackbarMessage('File Available for download.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      if (typeof url === 'string') {
        fetch(url)
          .then((response) => response.blob())
          .then((blob) => {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            const filename = url.split('/').pop() || 'downloaded-file';
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          })
          .catch(() => alert('Failed to download file'));
      }
    }

    if (getTaskStatusOutputJson === STATUS.FAILED) {
      setSnackbarMessage(getTaskStatusOutputJsonMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }

    return () => {
      if (getTaskStatusOutputJson === STATUS.SUCESS || getTaskStatusOutputJson === STATUS.FAILED) {
        dispatch(setTaskStatus(STATUS.NOT_STARTED));
      }
    };
  }, [dispatch, getTaskStatusOutputJson, getTaskStatusOutputJsonMessage, taskStatusOutputJsonResponse]);

  useEffect(() => {
    if (downloadAnalyticsByExpertStatus === STATUS.SUCESS) {
      setSnackbarMessage(getExpertAnalyticsDownloadMessage);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      const taskId = analyticsExpertDownload && typeof analyticsExpertDownload === 'object' && 'task_id' in analyticsExpertDownload
        ? (analyticsExpertDownload as { task_id: string }).task_id
        : undefined;
      if (taskId) {
        dispatch(getOutputJsonTaskStatus({ celery_task_id: taskId }));
      }
    }

    if (downloadAnalyticsByExpertStatus === STATUS.FAILED) {
      setSnackbarMessage('Couldn\'t Download File. Please Try Again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }

    return () => {
      if (downloadAnalyticsByExpertStatus === STATUS.SUCESS || downloadAnalyticsByExpertStatus === STATUS.FAILED) {
        dispatch(setDownloadAnalyticsByExpertStatus(STATUS.NOT_STARTED));
      }
    };
  }, [dispatch, analyticsExpertDownload, downloadAnalyticsByExpertStatus, getExpertAnalyticsDownloadMessage]);

  const tableColumns = [
    'Stage',
    'Allocated',
    'Completed',
    'Pending',
    'Accepted As-Is',
    'Accepted With Edits',
    'Rejected',
    'Accepted (Total)',
    'Productivity',
    'AHT',
    'Quality Score',
    'Error %',
  ];

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: '24px' }}>
        <Typography sx={{ marginLeft: '10px', fontFamily: 'Montserrat', fontWeight: 700, fontSize: '20px', lineHeight: '14px', marginBottom: '20px' }}>
          Task by Experts
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mr: 2 }}>
          <Tooltip title="Filter" placement="bottom-end" arrow>
            <IconButton
              aria-label="filter options"
              onClick={(event) => setFilterAnchorEl(event.currentTarget)}
              size="small"
              sx={{ width: 36, height: 36, borderRadius: '8px', color: 'white', backgroundColor: '#3D85C6', '&:hover': { backgroundColor: '#2F6EA8' } }}
            >
              <FilterAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <IconButton
            style={{ height: '40px', borderRadius: '10px', padding: '8px 6px', color: 'white', backgroundColor: downloadAnalyticsByExpertStatus === STATUS.PENDING ? '#dfdfdf' : '#3D85C6' }}
            onClick={handleMenuClick}
            disabled={downloadAnalyticsByExpertStatus === STATUS.PENDING}
            size='small'
          >
            {downloadAnalyticsByExpertStatus === STATUS.PENDING ? <CircularProgress size={25} /> : <DownloadIcon />}
          </IconButton>
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleDownloadForTask}>Download for Task</MenuItem>
          <MenuItem onClick={handleDownloadForReview}>Download for Review</MenuItem>
        </Menu>
      </div>

      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>Filters</Typography>

          <TextField size="small" label="Search" placeholder="Name or email" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />

          <TextField
            size="small"
            select
            label="Stage"
            value={selectedStageId}
            onChange={(event) => {
              setSelectedStageId(event.target.value);
              setCurrentPage(1);
            }}
          >
            <MenuItem value="">All Stages</MenuItem>
            {stageOptions.map((stage: any) => (
              <MenuItem key={stage.id} value={String(stage.id)}>{stage.name}</MenuItem>
            ))}
          </TextField>

          <TextField size="small" label="Start Date" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField size="small" label="End Date" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ textTransform: 'none' }}>Clear</Button>
            <Button variant="contained" size="small" onClick={handleApplyFilters} sx={{ textTransform: 'none' }}>Apply</Button>
          </Box>
        </Box>
      </Popover>

      {expertAnalyticsStatus === STATUS.FAILED ? (
        <Error message="Unable to load experts analytics. Please adjust filters and try again." />
      ) : (
        <Box sx={{ mt: 2, border: '1px solid #e5e7eb', borderRadius: '12px', backgroundColor: '#fff', p: 2, minHeight: 520 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '320px minmax(0, 1fr)', gap: 2 }}>
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '12px', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f1f5f9', fontWeight: 700, fontSize: '14px' }}>
                Experts List
              </Box>

              <Box sx={{ p: 1.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by name or Gig Source ID"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  sx={{ mb: 1.5 }}
                />

                {filteredExperts.length === 0 ? (
                  <Typography sx={{ color: '#64748b', fontSize: '13px', p: 1 }}>No experts found.</Typography>
                ) : (
                  <Box sx={{ border: '1px solid #dfe8f1', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '28px 1.7fr 1.3fr', borderBottom: '1px solid #dfe8f1', backgroundColor: '#f8fafc' }}>
                      <Box />
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#475569', p: '8px 10px' }}>Name</Typography>
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#475569', p: '8px 10px' }}>Gig Source ID</Typography>
                    </Box>

                    {filteredExperts.map((expert: any) => {
                      const isSelected = (expert?.expert_id || expert?.email) === (selectedExpert?.expert_id || selectedExpert?.email);
                      const primaryName = getExpertNameValue(expert);
                      const fallbackEmail = getExpertEmailValue(expert);
                      const displayName = primaryName || fallbackEmail || '—';
                      const gigId = String(getExpertGigId(expert));

                      return (
                        <Box
                          key={expert?.expert_id || expert?.email || `${expert?.name}-${expert?.stage}`}
                          onClick={() => setSelectedExpertId(expert?.expert_id || expert?.email || null)}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '28px 1.7fr 1.3fr',
                            borderBottom: '1px solid #edf2f7',
                            backgroundColor: isSelected ? '#e0f2fe' : 'transparent',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f0f9ff' },
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: isSelected ? '#0284c7' : 'transparent' }}>
                            <KeyboardArrowRightRoundedIcon fontSize="small" />
                          </Box>
                          <Box sx={{ p: '10px', minWidth: 0 }}>
                            <Typography sx={{ fontSize: '12px', color: '#0f172a', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {displayName}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '12px', color: '#2563eb', fontWeight: 600, p: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {gigId}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '12px', backgroundColor: '#fff', p: 2 }}>
              {expertAnalyticsStatus === STATUS.PENDING ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                  <CircularProgress />
                </Box>
              ) : selectedExpert ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                      {getExpertDisplayName(selectedExpert)} ({getExpertGigId(selectedExpert)})
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ display: 'flex', border: '1px solid #dbeafe', borderRadius: '8px', overflow: 'hidden' }}>
                        {(['day', 'week', 'month'] as AnalyticsGranularity[]).map((option) => (
                          <Button
                            key={option}
                            size="small"
                            onClick={() => {
                              const dateRange = getDateRangeForGranularity(option);
                              setGranularity(option);
                              setAppliedStartDate(dateRange.startDate);
                              setAppliedEndDate(dateRange.endDate);
                              setCurrentPage(1);
                            }}
                            sx={{
                              minWidth: 54,
                              borderRadius: 0,
                              textTransform: 'none',
                              fontSize: '12px',
                              backgroundColor: granularity === option ? '#e0f2fe' : '#fff',
                              color: granularity === option ? '#0ea5e9' : '#475569',
                            }}
                          >
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(130px, 1fr))', gap: 1.5, mb: 3 }}>
                    {[
                      { label: 'Project Score', value: formatScore(selectedExpert.project_score) },
                      { label: 'Quality Score', value: formatScore(selectedExpert.quality_score) },
                      { label: 'AHT', value: selectedExpert.aht ?? '—' },
                      { label: 'Error %', value: formatScore(selectedExpert.error_percentage ?? selectedExpert.error_rate) },
                    ].map((metric) => (
                      <Box key={metric.label} sx={{ border: '1px solid #e5e7eb', borderRadius: '10px', p: 1.5, backgroundColor: '#f8fafc' }}>
                        <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{metric.label}</Typography>
                        <Typography sx={{ mt: 0.5, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>{metric.value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                            {tableColumns.map((column) => (
                              <TableCell key={column} sx={{ fontSize: '10px', fontWeight: 700, whiteSpace: 'nowrap', color: '#475569', py: 1.25 }}>
                                {column}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stageScoreEntries.length > 0 ? (
                            stageScoreEntries.map((stage, index) => (
                              <TableRow key={`${stage.name}-${index}`} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a', fontWeight: 600 }}>{stage.name}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{selectedExpert.allocated ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{selectedExpert.completed ?? selectedExpert.total_completed ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{selectedExpert.pending ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{selectedExpert.total_quality_accepted_as_is ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{selectedExpert.total_quality_accepted_with_edits ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{selectedExpert.total_quality_rejected ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{getAcceptedTotal(selectedExpert)}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{selectedExpert.productivity ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{selectedExpert.aht ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a', fontWeight: 600 }}>{formatScore(stage.score)}</TableCell>
                                <TableCell sx={{ fontSize: '12px', color: '#0f172a' }}>{formatScore(selectedExpert.error_percentage ?? selectedExpert.error_rate)}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={12} sx={{ py: 3, textAlign: 'center', color: '#64748b' }}>
                                No stage scores available for this expert.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                  <Error message="" />
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Typography sx={{ color: '#64748b', fontSize: '12px' }}>
              {filteredExperts.length} expert{filteredExperts.length === 1 ? '' : 's'} shown
            </Typography>
          </Box>
        </Box>
      )}

      <SnackbarNotification open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
      <TaskStatusModal open={openTaskStatusModal} onClose={() => setOpenTaskStatusModal(false)} taskStatusData={taskStatusOutputJsonResponse?.data} taskByExperts={true} />
    </>
  );
};

export default TaskByExperts;